import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  createApiClient,
  type RegionSummaryResponseV1,
  type WorldTopologyManifestResponseV1,
} from "@planet-conflict/api-client";

const api = createApiClient({
  baseUrl:
    import.meta.env.VITE_API_BASE_URL ?? "https://api.planetconflict.com",
});

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "not-found" }
  | { readonly status: "unavailable"; readonly message: string }
  | {
      readonly status: "ready";
      readonly manifest: WorldTopologyManifestResponseV1;
      readonly regions: ReadonlyArray<RegionSummaryResponseV1>;
    };

type SortMode = "index" | "cells";
type MobileMode = "map" | "index";

const regionLabel = (index: number) =>
  `Region ${String(index + 1).padStart(3, "0")}`;

const project = (region: RegionSummaryResponseV1) => {
  const longitude = Math.atan2(region.centroidY, region.centroidX);
  const latitude = Math.asin(Math.max(-1, Math.min(1, region.centroidZ)));
  return {
    x: Math.max(
      10,
      Math.min(990, ((longitude + Math.PI) / (2 * Math.PI)) * 1000),
    ),
    y: Math.max(10, Math.min(490, ((Math.PI / 2 - latitude) / Math.PI) * 500)),
  };
};

const loadWorld = async (
  universeId: string,
  bodyId: string,
  signal: AbortSignal,
) => {
  const manifest = await api.worldTopology(universeId, bodyId, signal);
  const regions: RegionSummaryResponseV1[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  let hasMore = true;
  while (hasMore) {
    const page = await api.worldRegions(universeId, bodyId, cursor, signal);
    regions.push(...page.regions);
    if (!page.nextCursor) {
      hasMore = false;
      continue;
    }
    if (seenCursors.has(page.nextCursor))
      throw new Error("Region pagination cursor repeated.");
    seenCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }
  const unique = new Set(regions.map((region) => region.regionId.value));
  if (unique.size !== regions.length)
    throw new Error("Region projection contained duplicate IDs.");
  if (regions.length !== manifest.regionCount)
    throw new Error("Region projection did not match its activated manifest.");
  return { manifest, regions };
};

export const filterAndSortRegions = (
  regions: ReadonlyArray<RegionSummaryResponseV1>,
  query: string,
  sort: SortMode,
) => {
  const normalized = query.trim().toLowerCase();
  return [...regions]
    .filter(
      (region) =>
        normalized.length === 0 ||
        regionLabel(region.index).toLowerCase().includes(normalized) ||
        region.regionId.value.toLowerCase().includes(normalized),
    )
    .sort((left, right) =>
      sort === "cells"
        ? right.strategicCellCount - left.strategicCellCount ||
          left.index - right.index
        : left.index - right.index,
    );
};

export function WorldPage({
  universeId,
  bodyId,
}: {
  readonly universeId: string;
  readonly bodyId: string;
}) {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("index");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileMode, setMobileMode] = useState<MobileMode>("map");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    void loadWorld(universeId, bodyId, controller.signal)
      .then(({ manifest, regions }) => {
        setState({ status: "ready", manifest, regions });
        setSelectedId(regions[0]?.regionId.value ?? null);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.status === 404)
          setState({ status: "not-found" });
        else
          setState({
            status: "unavailable",
            message:
              "The activated world projection could not be loaded. No topology was fabricated.",
          });
      });
    return () => controller.abort();
  }, [attempt, bodyId, universeId]);

  const visible = useMemo(() => {
    if (state.status !== "ready") return [];
    return filterAndSortRegions(state.regions, query, sort);
  }, [query, sort, state]);
  const selected =
    state.status === "ready"
      ? (state.regions.find((region) => region.regionId.value === selectedId) ??
        null)
      : null;

  if (state.status === "loading")
    return (
      <WorldState
        title="Resolving world topology"
        message="Loading the activated manifest and canonical region pages…"
        busy
      />
    );
  if (state.status === "not-found")
    return (
      <WorldState
        title="Activated world not found"
        message="This universe and celestial-body scope is unknown, unpublished, or not yet activated."
      />
    );
  if (state.status === "unavailable")
    return (
      <WorldState
        title="World projection unavailable"
        message={state.message}
        action={
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry connection
          </button>
        }
      />
    );

  return (
    <main className="world-page" tabIndex={-1}>
      <a className="skip-link" href="#region-index">
        Skip to region index
      </a>
      <header className="world-header">
        <a className="wordmark" href="/foundation">
          <span className="brand-mark" aria-hidden="true">
            PC
          </span>
          <span>
            PLANET<em>CONFLICT</em>
          </span>
        </a>
        <div>
          <span className="section-label">
            ACTIVATED WORLD / {state.manifest.topologyProfile}
          </span>
          <h1>Strategic world survey</h1>
          <p>
            {state.manifest.regionCount} regions ·{" "}
            {state.manifest.strategicCellCount.toLocaleString()} cells ·{" "}
            {state.manifest.generatorVersion}
          </p>
        </div>
        <span className="catalog-badge ready">◇ HASH VERIFIED</span>
      </header>
      <div
        className="world-mobile-modes"
        role="group"
        aria-label="World presentation"
      >
        <button
          type="button"
          aria-pressed={mobileMode === "map"}
          onClick={() => setMobileMode("map")}
        >
          Map
        </button>
        <button
          type="button"
          aria-pressed={mobileMode === "index"}
          onClick={() => setMobileMode("index")}
        >
          Region index
        </button>
      </div>
      <div className={`world-workspace mode-${mobileMode}`}>
        <section
          className="projection-panel"
          aria-labelledby="projection-title"
        >
          <div className="world-panel-heading">
            <div>
              <span className="section-label">
                2D EQUIRECTANGULAR PROJECTION
              </span>
              <h2 id="projection-title">Region centroids</h2>
            </div>
            <span>{visible.length} shown</span>
          </div>
          <svg
            className="world-projection"
            viewBox="0 0 1000 500"
            role="group"
            aria-label="Interactive region centroid projection"
          >
            <defs>
              <pattern
                id="survey-grid"
                width="125"
                height="125"
                patternUnits="userSpaceOnUse"
              >
                <path d="M 125 0 L 0 0 0 125" />
              </pattern>
            </defs>
            <rect width="1000" height="500" className="projection-ocean" />
            <rect
              width="1000"
              height="500"
              fill="url(#survey-grid)"
              className="projection-grid"
            />
            <path d="M0 250H1000M500 0V500" className="projection-axis" />
            {visible.map((region) => {
              const point = project(region);
              const isSelected = region.regionId.value === selectedId;
              return (
                <circle
                  key={region.regionId.value}
                  cx={point.x}
                  cy={point.y}
                  r={isSelected ? 10 : 5}
                  className={
                    isSelected ? "region-marker selected" : "region-marker"
                  }
                  role="button"
                  tabIndex={0}
                  aria-label={`${regionLabel(region.index)}, ${region.strategicCellCount} strategic cells`}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedId(region.regionId.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedId(region.regionId.value);
                    }
                  }}
                />
              );
            })}
          </svg>
          <p className="projection-note">
            Presentation-only centroid survey. Authoritative boundaries,
            terrain, ownership, and resources are not yet available.
          </p>
        </section>
        <section
          className="world-index-panel"
          id="region-index"
          aria-labelledby="index-title"
        >
          <div className="world-panel-heading">
            <div>
              <span className="section-label">STRUCTURED ALTERNATIVE</span>
              <h2 id="index-title">Strategic region index</h2>
            </div>
          </div>
          <div className="region-controls">
            <label>
              Search regions
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Region number or stable ID"
              />
            </label>
            <label>
              Sort by
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortMode)}
              >
                <option value="index">Region number</option>
                <option value="cells">Cell count</option>
              </select>
            </label>
          </div>
          <div className="region-table-scroll">
            <table className="world-region-table">
              <caption>
                {visible.length} of {state.regions.length} activated regions
              </caption>
              <thead>
                <tr>
                  <th scope="col">Region</th>
                  <th scope="col">Cells</th>
                  <th scope="col">Stable identifier</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((region) => (
                  <tr
                    key={region.regionId.value}
                    className={
                      region.regionId.value === selectedId ? "selected" : ""
                    }
                    aria-selected={region.regionId.value === selectedId}
                  >
                    <th scope="row">
                      <button
                        type="button"
                        onClick={() => setSelectedId(region.regionId.value)}
                      >
                        {regionLabel(region.index)}
                      </button>
                    </th>
                    <td>{region.strategicCellCount}</td>
                    <td>
                      <code>{region.regionId.value}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {visible.length === 0 && (
            <p className="world-empty" role="status">
              No activated regions match this search.
            </p>
          )}
        </section>
        <aside
          className="region-detail"
          aria-live="polite"
          aria-label="Selected region details"
        >
          {selected ? (
            <>
              <span className="section-label">SELECTED REGION</span>
              <h2>{regionLabel(selected.index)}</h2>
              <dl>
                <div>
                  <dt>Strategic cells</dt>
                  <dd>{selected.strategicCellCount}</dd>
                </div>
                <div>
                  <dt>Stable ID</dt>
                  <dd>
                    <code>{selected.regionId.value}</code>
                  </dd>
                </div>
                <div>
                  <dt>Centroid</dt>
                  <dd>
                    {selected.centroidX.toFixed(4)},{" "}
                    {selected.centroidY.toFixed(4)},{" "}
                    {selected.centroidZ.toFixed(4)}
                  </dd>
                </div>
              </dl>
              <p>
                No ownership, terrain, resources, or population projection
                exists yet.
              </p>
            </>
          ) : (
            <p>Select a region from the map or index.</p>
          )}
        </aside>
      </div>
      <p className="sr-only" aria-live="polite">
        Loaded {state.regions.length} activated regions.
      </p>
    </main>
  );
}

function WorldState({
  title,
  message,
  busy = false,
  action,
}: {
  readonly title: string;
  readonly message: string;
  readonly busy?: boolean;
  readonly action?: React.ReactNode;
}) {
  return (
    <main className="world-state" tabIndex={-1} aria-busy={busy}>
      <a className="wordmark" href="/foundation">
        <span className="brand-mark" aria-hidden="true">
          PC
        </span>
        <span>
          PLANET<em>CONFLICT</em>
        </span>
      </a>
      <section>
        <span className="section-label">WORLD SURVEY</span>
        <h1>{title}</h1>
        <p>{message}</p>
        {action}
      </section>
    </main>
  );
}
